/**
 * IP-level zone deduplication — mirrors Connector face folding (UPnP over RAAT).
 */
import type { Zone } from './rendererZoneMap';

/** UPnP playback face wins over RAAT/psaudio at the same IP. */
export const ZONE_PROTOCOL_RANK: Record<string, number> = {
  upnp: 100,
  psaudio: 70,
  lua: 60,
  manual: 55,
  chromecast: 50,
  airplay: 40,
  browser: 30,
};

export type ZoneDedupResult = {
  zones: Zone[];
  /** dropped id → canonical id at the same IP */
  foldedTo: Record<string, string>;
};

function isValidDedupIp(ip: string | undefined): ip is string {
  if (!ip) return false;
  const trimmed = ip.trim();
  return trimmed.length > 0
    && trimmed !== '127.0.0.1'
    && trimmed !== '0.0.0.0'
    && trimmed !== 'undefined'
    && trimmed !== 'null';
}
export const hostsMayFold = (a?: string, b?: string): boolean => !!a && isValidDedupIp(a) && a === b;

/**
 * Dedup zones at the same IP. When merging, UPnP ranks above psaudio/RAAT.
 */
export function deduplicateZones(zones: Zone[]): ZoneDedupResult {
  const ipGroups = new Map<string, Zone[]>();
  const passThrough: Zone[] = [];
  const foldedTo: Record<string, string> = {};

  for (const zone of zones) {
    if (zone.type === 'browser' || zone.id === 'local_computer') {
      passThrough.push(zone);
      continue;
    }

    const ip = zone.host?.trim();
    if (isValidDedupIp(ip)) {
      if (!ipGroups.has(ip)) ipGroups.set(ip, []);
      ipGroups.get(ip)!.push(zone);
    } else {
      passThrough.push(zone);
    }
  }

  const result: Zone[] = [...passThrough];
  for (const [ip, group] of ipGroups) {
    if (group.length === 1) {
      result.push(group[0]);
      continue;
    }

    group.sort((a, b) =>
      (ZONE_PROTOCOL_RANK[b.type] ?? 0) - (ZONE_PROTOCOL_RANK[a.type] ?? 0));
    const winner = group[0];
    for (const dropped of group.slice(1)) {
      foldedTo[dropped.id] = winner.id;
    }
    const droppedSummary = group.slice(1).map((z) => `${z.name}(${z.type})`).join(', ');
    console.log(
      `[zone-dedup] @${ip}: keeping ${winner.name} (${winner.type}), dropped: ${droppedSummary}`,
    );
    result.push(winner);
  }

  return { zones: result, foldedTo };
}

export function mergeFoldedToMaps(
  existing: Record<string, string>,
  incoming: Record<string, string>,
): Record<string, string> {
  const merged = { ...existing, ...incoming };
  let changed = true;
  while (changed) {
    changed = false;
    for (const [from, to] of Object.entries(merged)) {
      const chained = merged[to];
      if (chained && chained !== to) {
        merged[from] = chained;
        changed = true;
      }
    }
  }
  return merged;
}

export function resolveZoneIdViaAliases(
  zoneId: string,
  aliases: Record<string, string>,
): string {
  let current = zoneId;
  const seen = new Set<string>();
  while (aliases[current] && !seen.has(current)) {
    seen.add(current);
    current = aliases[current];
  }
  return current;
}
